/* import __COLOCATED_TEMPLATE__ from './agent-type-grouping-filter.hbs'; */
/* RESPONSIBLE TEAM: team-reporting */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { REPORTING_FILTER_SELECT_ALL } from 'embercom/lib/reporting/flexible/constants';
import type IntlService from 'ember-intl/services/intl';
import type { SelectedFilter } from 'embercom/services/filter-pattern-service';

interface Args {
  selected: SelectedFilter;
  loadKnownValuesSources: string[];
  analyticsObject: string;
  setSelected: (filterValues: string[], operator?: string) => void;
  removeFilter: () => void;
  initiallyOpened: boolean;
  icon: string;
  type: string;
  name: string;
  isDisabled: boolean;
}

interface Item {
  text: string;
  value: string;
  isSelected: boolean;
}

export default class AgentTypeGroupingFilter extends Component<Args> {
  @service declare appService: $TSFixMe;
  @service declare intercomEventService: $TSFixMe;
  @service declare intl: IntlService;
  @tracked selectedDropdownItems = this.args.selected.values || [];
  @tracked operator = this.args.selected.operator || 'category';
  @tracked label = this.currentLabel;

  constructor(owner: unknown, args: $TSFixMe) {
    super(owner, args);
  }

  get agentTypeGroupingLabels() {
    return {
      'ai-agent': this.intl.t(
        'components.reporting.custom.chart-builder.filter-bar.agent-type-grouping-filter.ai-agent',
      ),
      human: this.intl.t(
        'components.reporting.custom.chart-builder.filter-bar.agent-type-grouping-filter.human',
      ),
      'human_and_ai-agent': this.intl.t(
        'components.reporting.custom.chart-builder.filter-bar.agent-type-grouping-filter.human_and_ai-agent',
      ),
      workflow: this.intl.t(
        'components.reporting.custom.chart-builder.filter-bar.agent-type-grouping-filter.workflow',
      ),
    } as const;
  }

  get allItems(): Item[] {
    return Object.entries(this.agentTypeGroupingLabels).map(([value, text]) => ({
      text,
      value,
      isSelected: this.selectedDropdownItems.includes(value),
    }));
  }

  get selectedAgentTypeGroupings() {
    return this.selectedDropdownItems.filter((item) => item !== REPORTING_FILTER_SELECT_ALL);
  }

  get currentLabel() {
    if (this.selectedAgentTypeGroupings.length === 0) {
      return this.intl.t(
        'components.reporting.custom.chart-builder.filter-bar.agent-type-grouping-filter.any',
      );
    }

    let agents = this.selectedAgentTypeGroupings.map((agent) => {
      return this.agentTypeGroupingLabels[agent as keyof typeof this.agentTypeGroupingLabels];
    });
    return `${agents.join(', ')}`;
  }

  @action
  trackAnalytics() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'filtered_agent_type_grouping',
      object: this.args.analyticsObject,
      agent_type_groupings: this.selectedAgentTypeGroupings,
      operator: this.operator,
    });
  }

  @action
  onClose() {
    if (this.isDestroying) {
      return;
    }

    this.label = this.currentLabel;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Custom::ChartBuilder::FilterBar::AgentTypeGroupingFilter': typeof AgentTypeGroupingFilter;
    'reporting/custom/chart-builder/filter-bar/agent-type-grouping-filter': typeof AgentTypeGroupingFilter;
  }
}
